import { useMemo } from 'react';

import { MATERIALS } from '@/game/data/progression';
import { ROCK_CELL, ROCK_SIZE, ROCK_URL, rockFrame } from '@/game/data/rockAtlas';
import { anchorOffset, clipFor, embedding, type Embedding } from '@/game/engine/field';
import { materialIndexAt } from '@/game/engine/materials';
import { project, type Viewport } from '@/game/engine/projection';
import { seamStyle } from './RockField';

/**
 * Rocks embedded in the corridor walls — at least three visible at every step.
 *
 * The cave used to show exactly one rock: the one in your path. Between spawns
 * (one every 3–6 steps) there was nothing but rings, so a mine read as an empty
 * pipe and descending felt like the walls were being swapped rather than passed.
 *
 * These are **scenery, not loot**, and that distinction is deliberate:
 *
 * - Energy caps how many rocks you can BANK (one per claim, see run_engine.claim).
 *   Making these breakable would not give the player more — it would drain the
 *   same 25 energy in eight steps instead of forty, and make a session end sooner.
 *   The complaint that a run is over too quickly gets worse, not better.
 * - They are derived from depth, never stored, exactly like CaveDecor. Nothing
 *   here touches the run, so there is nothing to desync and no way for a client
 *   to invent a reward. The path rock stays the only thing the server knows about.
 *
 * They reuse the real material sheet (`rocks.webp`, ten materials × five crack
 * stages), always at stage 0 — an untouched rock in the wall. Which material
 * appears is picked from DEPTH, so the walls change as you descend and agree with
 * the material the path rock is made of at that depth.
 */

/** Deterministic 0–1 from any pair of integers. Same hash CaveDecor uses. */
function noise(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) >>> 0;
  h = ((h ^ (h >>> 13)) * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

const COLS = ROCK_SIZE.width / ROCK_CELL;
const ROWS = ROCK_SIZE.height / ROCK_CELL;

/** How many wall rocks are in view at once. Three is the floor, not the target. */
const COUNT = 10;

/**
 * How many metres into a new 20 m band the previous band's set keeps fading out.
 *
 * Below this the re-dress was a hard pop: every rock in view jumped to a new
 * position and material on the single frame `depth` crossed a multiple of 20,
 * because the whole array re-seeds atomically. Crossfading the outgoing band
 * over the first couple of metres of the new one costs one extra `useMemo`
 * array near a boundary and nothing the rest of the time — this is scenery
 * that was already discarded and rebuilt every band, never state.
 */
export const FADE_M = 2;

interface WallRock {
  key: string;
  u: number;
  v: number;
  d: number;
  size: number;
  spin: number;
  flip: number;
  hue: number;
  /** Deterministic per-rock seed for `embedding`'s jitter — keyed on band and
      index, never on raw depth, so the wall cut does not twitch mid-step. */
  seed: string;
}

/**
 * Which materials belong at this depth: the one the depth has reached, plus the
 * two before it. A single material would read as a tiled wall; all ten would read
 * as a mineral catalogue.
 *
 * `materialIndexAt` is the same curve `run_engine.roll_material` rolls against
 * server-side (`game/engine/materials.ts`, generated from the prototype) — this
 * used to guess with a flat `depth / 90`, so a wall of Basalt could dress a
 * corridor whose real, tappable rocks were still rolling Granite. Scenery must
 * agree with gameplay about what depth it is.
 */
function paletteFor(depth: number): string[] {
  const reached = materialIndexAt(depth);
  const from = Math.max(0, reached - 2);
  return MATERIALS.slice(from, reached + 1).map((m) => m.key);
}

/**
 * One band's worth of scenery. Pulled out of the component so the fading-out
 * PREVIOUS band can be built with the same rule as the current one — two calls
 * to one pure function, not a second copy of the layout maths.
 */
function buildRocks(band: number, palette: string[]): WallRock[] {
  return Array.from({ length: COUNT }, (_, i) => {
    const a = noise(i, band);
    const b = noise(i + 71, band);
    const c = noise(i + 143, band);

    /* Pushed to the SIDES: |u| >= 0.58 keeps them out of the walking line and
       off the path rock, which owns the middle. CaveDecor holds the same rule
       for the same reason. */
    const side = a < 0.5 ? -1 : 1;
    const u = side * (0.58 + 0.34 * b);

    return {
      key: `renders/rock_${palette[Math.floor(c * palette.length) % palette.length]}_0`,
      u,
      /* Spread from floor to ceiling, biased low — rubble collects at the bottom. */
      v: 0.10 + 0.72 * noise(i + 211, band) ** 1.35,
      /* Staggered through the visible depth so they parallax past at different
         rates. That is what makes a step read as travel. */
      d: 3 + 30 * ((i / COUNT + 0.11 * noise(i + 307, band)) % 1),
      size: 0.34 + 0.5 * noise(i + 401, band),
      spin: -18 + 36 * noise(i + 509, band),
      /* Mirrored at random. The atlas has one render per material per stage,
         so with enough rocks in view a repeated sprite was recognisable as
         the same asset reused — a flip is free variety from the art already
         on hand, same trick RockField uses for the field rocks. */
      flip: noise(i + 601, band) < 0.5 ? -1 : 1,
      /* A few degrees either way so a wall of the same material doesn't
         read as one rock copy-pasted along the tunnel. */
      hue: -6 + 12 * noise(i + 701, band),
      seed: `wall_${band}_${i}`,
    };
  });
}

/**
 * Where `depth` sits inside its 20 m band, and whether the outgoing band is
 * still fading out. Pulled out of the component so the crossfade window can be
 * asserted directly rather than through a render.
 */
export function bandTransition(depth: number): { band: number; fading: boolean; fadeIn: number } {
  const band = Math.floor(depth / 20);
  const bandFrac = depth - band * 20;
  /* Only near a boundary — and only past the first band, since there is no
     "band -1" before the mine starts — is the outgoing set built at all. The
     rest of a 20 m stretch costs exactly what it always did: one array. */
  return { band, fading: bandFrac < FADE_M && band > 0, fadeIn: Math.min(1, bandFrac / FADE_M) };
}

export function WallRocks({ depth, view, lightPx }: {
  depth: number;
  view: Viewport;
  lightPx: number;
}) {
  const { current, previous, fadeIn } = useMemo(() => {
    /* Re-dress every 20 m, the same band CaveDecor uses, so decor and wall rocks
       change together instead of drifting in and out of step with each other. */
    const { band, fading, fadeIn } = bandTransition(depth);
    const palette = paletteFor(depth);

    return {
      current: buildRocks(band, palette),
      previous: fading ? buildRocks(band - 1, palette) : null,
      fadeIn,
    };
  }, [depth]);

  if (!view.width) return null;

  return (
    <>
      {current.map((r, i) => renderRock(r, `c${i}`, fadeIn, view, lightPx))}
      {previous?.map((r, i) => renderRock(r, `p${i}`, 1 - fadeIn, view, lightPx))}
    </>
  );
}

/**
 * One scenery rock, embedded in the wall it grew in — RockField's own
 * embedding/clip/seam machinery, reused rather than re-derived. `fadeMul`
 * layers a crossfade opacity on top of the usual depth fog, so a rock fading
 * out of the outgoing band and a rock fading into the new one cross rather
 * than pop.
 */
function renderRock(
  r: WallRock, key: string, fadeMul: number, view: Viewport, lightPx: number,
) {
  if (fadeMul <= 0.002) return null;

  const p = project(r.u, r.v, r.d, view, lightPx);
  const frame = rockFrame(r.key);
  if (!frame) return null;

  const px = COLS > 1 ? (frame.x / (ROCK_SIZE.width - ROCK_CELL)) * 100 : 0;
  const py = ROWS > 1 ? (frame.y / (ROCK_SIZE.height - ROCK_CELL)) * 100 : 0;
  const size = Math.max(10, p.scale * view.width * r.size * 0.30);

  /* Cut into the wall the rock actually sits in, the same way RockField embeds
     a real spawn — these always sit at |u| >= 0.58, so always a side wall,
     never floor or ceiling. Undrawn as a full square these read as decals
     stuck to the wall rather than stone growing out of it; see field.ts. */
  const embed: Embedding | null = embedding('wall', r.u, r.seed);
  const { offX, offY } = anchorOffset(embed, size, false);

  return (
    <div
      key={key}
      className="pointer-events-none absolute"
      aria-hidden
      style={{
        /* anchorOffset, not translate(-50%, -50%) — the cut has to land
           exactly on the projected wall point, and EMBED_FRACTION is not a
           half, so centring the box would leave the cut floating off the
           wall normal instead of sitting on it. */
        left: p.x + offX,
        top: p.y + offY,
        width: size,
        height: size,
        /* Behind the path rock (z 6) and the miner, in front of the rings. */
        zIndex: 3,
        /* Dimmer than the path rock and dimmer still with distance: these
           must never compete with the thing the player can actually hit.
           `fadeMul` only matters within FADE_M of a band boundary — the
           rest of the time it is exactly 1 and changes nothing. */
        opacity: p.opacity * 0.72 * fadeMul,
        /* The buried half of the rock is not drawn at all — same clip
           RockField cuts real spawns with. */
        clipPath: clipFor(embed),
        WebkitClipPath: clipFor(embed),
      }}
    >
      {/* The stone itself. Separate from the clipped wrapper because CSS
          applies clip-path before transforms — spin and flip belong to the
          rock, the cut belongs to the wall, and the wall does not turn with
          the rock (see RockField.tsx). */}
      <i
        aria-hidden
        className="absolute inset-0 block"
        style={{
          transform: `rotate(${r.spin}deg) scaleX(${r.flip})`,
          WebkitTransform: `rotate(${r.spin}deg) scaleX(${r.flip})`,
          backgroundImage: `url(${ROCK_URL})`,
          backgroundSize: `${COLS * 100}% ${ROWS * 100}%`,
          backgroundPosition: `${px}% ${py}%`,
          backgroundRepeat: 'no-repeat',
          /* Same warm shift the tappable field rocks get, one stop darker.
             brightness(.62) alone was still lighter than the wall behind it,
             so the ceiling — where these cluster thickest — became the
             brightest region of the frame and pulled the eye off the rock the
             player can actually hit. Scenery must lose to gameplay. The
             per-rock hue jitter is the same idea as the flip above: cheap
             variety from one render so a repeated material doesn't look
             stamped from the same tile. */
          filter: `brightness(.46) saturate(.88) sepia(.18) hue-rotate(${(-8 + r.hue).toFixed(1)}deg)`,
        }}
      />

      {/* The seam where the stone meets the face — same lit lip RockField
          gives a real spawn, so scenery and loot read as the same wall. */}
      {embed && (
        <i aria-hidden className="pointer-events-none absolute block" style={seamStyle(embed, size)} />
      )}
    </div>
  );
}
